import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharedCredentials, setSharedCredentials] = useState([]);
  const [sharedCredentialsLoaded, setSharedCredentialsLoaded] = useState(false);
  const isLoggingOutRef = useRef(false);

  // Logout function that can be called from interceptor
  const performLogout = () => {
    if (isLoggingOutRef.current) {
      return; // Prevent multiple simultaneous logout calls
    }
    
    isLoggingOutRef.current = true;
    console.log('[AUTH] Session expired (401 response), logging out...');
    
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    
    // Clear state
    setToken(null);
    setUser(null);
    setSharedCredentials([]);
    setSharedCredentialsLoaded(false);
    
    // Remove axios authorization header
    delete axios.defaults.headers.common['Authorization'];
    
    // Redirect to login page
    // Use window.location to ensure full page reload and clear any cached state
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    
    // Reset flag after a delay
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  };

  useEffect(() => {
    // Set up axios response interceptor to handle 401 (unauthorized) responses
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // Success response - just pass it through
        return response;
      },
      (error) => {
        // Check if it's a 401 error from api.syberkey.com or local API
        if (error.response && error.response.status === 401) {
          const errCode = error.response?.data?.error;
          const businessLogicErrors = ['security_questions_incorrect', 'biometric_verification_failed'];
          if (businessLogicErrors.includes(errCode)) {
            return Promise.reject(error);
          }

          const requestUrl = error.config?.url || '';
          const baseURL = error.config?.baseURL || '';
          
          // Construct full URL - handle both absolute URLs and relative URLs with baseURL
          let fullUrl = '';
          if (requestUrl.startsWith('http://') || requestUrl.startsWith('https://')) {
            fullUrl = requestUrl;
          } else if (baseURL) {
            fullUrl = `${baseURL}${requestUrl}`;
          } else {
            fullUrl = requestUrl;
          }
          
          // Only handle 401 from api.syberkey.com or localhost:3000 (our API endpoints)
          // Skip handling 401 from other domains (like external APIs)
          const isOurAPI = fullUrl.includes('api.syberkey.com') || 
                          fullUrl.includes('localhost:3000') ||
                          (baseURL && (baseURL.includes('api.syberkey.com') || baseURL.includes('localhost:3000')));
          
          if (isOurAPI) {
            console.log('[AUTH] 401 Unauthorized response detected from API, session expired');
            console.log('[AUTH] Request URL:', fullUrl);
            console.log('[AUTH] Base URL:', baseURL || 'none');
            
            // Perform logout and redirect
            performLogout();
            
            // Return a rejected promise to prevent the original error from propagating
            return Promise.reject(new Error('Session expired. Please login again.'));
          }
        }
        
        // For all other errors, pass them through
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }
    setLoading(false);
  }, []);

  const refreshSharedCredentials = useCallback(async () => {
    const t = token || localStorage.getItem('authToken');
    if (!t) {
      setSharedCredentials([]);
      setSharedCredentialsLoaded(true);
      return;
    }
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/user/shared-credentials`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const list = Array.isArray(res.data) ? res.data : res.data?.sharedCredentials || [];
      setSharedCredentials(list);
    } catch {
      setSharedCredentials([]);
    } finally {
      setSharedCredentialsLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setSharedCredentials([]);
      setSharedCredentialsLoaded(true);
      return;
    }
    setSharedCredentialsLoaded(false);
    refreshSharedCredentials();
  }, [token, refreshSharedCredentials]);

  const login = (newToken, userData) => {
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('authUser', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setSharedCredentialsLoaded(false);
    axios
      .get(`${API_CONFIG.BASE_URL}/user/shared-credentials`, {
        headers: { Authorization: `Bearer ${newToken}` },
      })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.sharedCredentials || [];
        setSharedCredentials(list);
      })
      .catch(() => setSharedCredentials([]))
      .finally(() => setSharedCredentialsLoaded(true));
  };

  const logout = () => {
    isLoggingOutRef.current = true;
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
    setSharedCredentials([]);
    setSharedCredentialsLoaded(false);
    delete axios.defaults.headers.common['Authorization'];
    
    // Redirect to login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  };

  const tenantAdminTenants = sharedCredentials.filter((c) => c.is_admin === true);

  const authContextValue = {
    user,
    token,
    login,
    logout,
    loading,
    sharedCredentials,
    sharedCredentialsLoaded,
    tenantAdminTenants,
    refreshSharedCredentials,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

